import React, {useState} from 'react';
import {Alert, FlatList} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Modal from 'react-native-modal';
import {NavigationContainerRef} from '@react-navigation/native';
import {PanGestureHandler} from 'react-native-gesture-handler';
import Animated, {
  useAnimatedGestureHandler,
  useSharedValue,
  useAnimatedStyle,
} from 'react-native-reanimated';
import {
  Container,
  IconDev,
  BtnIcon,
  ModalContainer,
  Card,
  Header,
  HeaderIconWrap,
  HeaderIcon,
  HeaderTitle,
  HeaderSubtitle,
  CloseButton,
  CloseIcon,
  HeaderSeparator,
  Body,
  SectionLabel,
  SectionRow,
  MockCountBadge,
  MockCountText,
  RouteChip,
  RouteChipText,
  InputWrapper,
  LabelInput,
  SaveButton,
  SaveButtonText,
  NoRouteBox,
  NoRouteIcon,
  NoRouteText,
  Divider,
  EmptyBox,
  EmptyIcon,
  EmptyText,
  MockItem,
  MockIconWrap,
  MockScreenIcon,
  MockInfo,
  MockLabel,
  MockMeta,
  MockActions,
  NavigateButton,
  NavigateIcon,
  DeleteButton,
  DeleteIcon,
} from './DevMocker.styles';

const STORAGE_KEY = '@dev_screen_mocks';

interface ScreenMock {
  id: string;
  screenName: string;
  params: Record<string, unknown>;
  label: string;
  savedAt: string;
}

interface Props {
  navigationRef: React.RefObject<NavigationContainerRef<any>>;
}

export function DevMocker({navigationRef}: Props) {
  const [showModal, setShowModal] = useState(false);
  const [mocks, setMocks] = useState<ScreenMock[]>([]);
  const [currentRoute, setCurrentRoute] = useState<{
    name: string;
    params: any;
  } | null>(null);
  const [label, setLabel] = useState('');

  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);

  const onGestureEvent = useAnimatedGestureHandler({
    onStart: (_, ctx: any) => {
      ctx.startX = translateX.value;
      ctx.startY = translateY.value;
    },
    onActive: (event, ctx) => {
      translateX.value = ctx.startX + event.translationX;
      translateY.value = ctx.startY + event.translationY;
    },
  });

  const animatedStyle = useAnimatedStyle(() => ({
    // @ts-ignore
    transform: [{translateX: translateX.value}, {translateY: translateY.value}],
  }));

  async function loadMocks(): Promise<ScreenMock[]> {
    try {
      const stored = await AsyncStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  async function persistMocks(newMocks: ScreenMock[]) {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(newMocks));
    setMocks(newMocks);
  }

  async function handleOpen() {
    const route = navigationRef.current?.getCurrentRoute();
    const routeData = route
      ? {name: route.name, params: route.params ?? {}}
      : null;
    setCurrentRoute(routeData);
    setLabel(route?.name ?? '');
    const stored = await loadMocks();
    setMocks(stored);
    setShowModal(true);
  }

  async function handleSave() {
    if (!currentRoute) {return;}
    const newMock: ScreenMock = {
      id: Date.now().toString(),
      screenName: currentRoute.name,
      params: currentRoute.params ?? {},
      label: label.trim() || currentRoute.name,
      savedAt: new Date().toISOString(),
    };
    const stored = await loadMocks();
    await persistMocks([newMock, ...stored]);
  }

  function handleNavigate(mock: ScreenMock) {
    setShowModal(false);
    try {
      navigationRef.current?.navigate(mock.screenName, mock.params);
    } catch {
      Alert.alert('Erro', `Não foi possível navegar para "${mock.screenName}"`);
    }
  }

  async function handleDelete(id: string) {
    const stored = await loadMocks();
    await persistMocks(stored.filter(m => m.id !== id));
  }

  function formatDate(iso: string) {
    const d = new Date(iso);
    return `${d.toLocaleDateString('pt-BR')} · ${d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }

  return (
    <Container>
      <PanGestureHandler onGestureEvent={onGestureEvent}>
        <Animated.View style={animatedStyle}>
          <IconDev onPress={handleOpen} activeOpacity={0.7}>
            <BtnIcon name="bookmark" />
          </IconDev>
        </Animated.View>
      </PanGestureHandler>

      <ModalContainer>
        <Modal
          isVisible={showModal}
          hasBackdrop
          onSwipeComplete={() => setShowModal(false)}
          onDismiss={() => setShowModal(false)}
          onBackdropPress={() => setShowModal(false)}
          style={{justifyContent: 'center', margin: 0}}>
          <Card>
            {/* ── Header ── */}
            <Header>
              <HeaderIconWrap>
                <HeaderIcon name="bookmark" />
              </HeaderIconWrap>
              <HeaderTitle>
                Mocks de Tela{'\n'}
                <HeaderSubtitle>Navegação rápida</HeaderSubtitle>
              </HeaderTitle>
              <CloseButton onPress={() => setShowModal(false)} activeOpacity={0.7}>
                <CloseIcon name="times" />
              </CloseButton>
            </Header>

            <HeaderSeparator />

            <Body>
              {/* ── Tela atual ── */}
              <SectionLabel>Tela atual</SectionLabel>

              {currentRoute ? (
                <>
                  <RouteChip>

                    <RouteChipText>{currentRoute.name}</RouteChipText>
                  </RouteChip>

                  <InputWrapper>

                    <LabelInput
                      placeholder="Label do mock..."
                      placeholderTextColor="#aaa"
                      value={label}
                      onChangeText={setLabel}
                      autoCapitalize="none"
                    />
                  </InputWrapper>

                  <SaveButton onPress={handleSave} activeOpacity={0.85}>

                    <SaveButtonText>Salvar mock</SaveButtonText>
                  </SaveButton>
                </>
              ) : (
                <NoRouteBox>
                  <NoRouteIcon name="exclamation-circle" />
                  <NoRouteText>
                    Não foi possível detectar a tela atual.
                  </NoRouteText>
                </NoRouteBox>
              )}

              <Divider />

              {/* ── Mocks salvos ── */}
              <SectionRow>
                <SectionLabel style={{marginBottom: 0}}>Mocks salvos</SectionLabel>
                {mocks.length > 0 && (
                  <MockCountBadge>
                    <MockCountText>{mocks.length}</MockCountText>
                  </MockCountBadge>
                )}
              </SectionRow>

              {mocks.length === 0 ? (
                <EmptyBox>
                  <EmptyIcon name="bookmark-o" />
                  <EmptyText>Nenhum mock salvo ainda.</EmptyText>
                </EmptyBox>
              ) : (
                <FlatList
                  data={mocks}
                  keyExtractor={item => item.id}
                  scrollEnabled={false}
                  renderItem={({item}) => (
                    <MockItem>
                      <MockIconWrap>
                        <MockScreenIcon name="mobile" />
                      </MockIconWrap>
                      <MockInfo onPress={() => handleNavigate(item)}>
                        <MockLabel>{item.label}</MockLabel>
                        <MockMeta>
                          {item.screenName} · {formatDate(item.savedAt)}
                        </MockMeta>
                      </MockInfo>
                      <MockActions>
                        <NavigateButton
                          onPress={() => handleNavigate(item)}
                          activeOpacity={0.8}>
                          <NavigateIcon name="arrow-right" />
                        </NavigateButton>
                        <DeleteButton
                          onPress={() => handleDelete(item.id)}
                          activeOpacity={0.7}>
                          <DeleteIcon name="trash" />
                        </DeleteButton>
                      </MockActions>
                    </MockItem>
                  )}
                />
              )}
            </Body>
          </Card>
        </Modal>
      </ModalContainer>
    </Container>
  );
}
